import type { Dispatch, Action } from 'redux';
import type {
  ExtractReducersAndEffects,
  ExtractStates,
  ConvertDispatchFromRematch,
} from './types';
import abTest from './ABTest';
import booking from './Booking.model';
import clientNavBar from './ClientNavBar.model';
import clientPromotions from './ClientPromotions.model';
import favorites from './Favorites.model';
import loader from './Loader.model';
import location from './Location.model';
import marketplaceProfile from './MarketplaceProfile/MarketplaceProfile.model';
import ncdAndDiscountedPros from './NCDAndDiscountedPros.model';
import netEarnings from './NetEarnings.model';
import { PaymentMethods as paymentMethods } from './PaymentMethods';
import { MarketingVideos } from './MarketingVideos';
import portals from './Portals.model';
import previousAppointments from './PreviousAppointments.model';
import providerAvailabilityBadgesFromSearch from './ AvailabilityBadges.model';
import { providerCalendar } from './ProviderCalendar';
import providerGiftCard from './ProviderGiftCard.model';
import { ProviderOnboarding as providerOnboarding } from './ProviderOnboarding.model';
import providerProducts from './ProviderProducts.model';
import providerReviews from './ProviderReviews.model';
import providers from './Providers.model';
import providerAvailability from './ProviderAvailability/ProviderAvailability.model';
import { providerClients } from './ProviderClients';
import profileGalleries from './ProfileGalleries.model';
import { profileServiceGroups } from './ProfileServiceGroups';
import providerServiceToPageMapping from './ProviderServiceToPageMapping.model';
import providerTopServices from './TopServices.model';
import providerWorkSchedule from './ProviderWorkSchedule.model';
import ratingsSummary from './RatingsSummary.model';
import recentSearches from './RecentSearches.model';
import recommendations from './Recommendations.model';
import referrals from './Referrals.model';
import route from './RouteState.model';
import search from './Search.model';
import searchConfig from './SearchConfig.model';
import seoData from './SEOData.model';
import showIncentives from './ShowIncentives.model';
import sidebar from './Sidebar.model';
import snackbar from './Snackbar.model';
import stripeIdentityVerification from './StripeIdentityVerification.model';
import { stripeTerminal } from './StripeTerminal';
import subsBlocker from './SubsBlocker.model';
import trackingId from './TrackingId.model';
import user from './CurrentUser.model';
import userAppointments from './UserAppointments';
import proCheckoutSettings from './ProviderCheckoutSettings/ProviderCheckoutSettings.model';
import providerSearchRank from './ProviderSearchRank.model';
import { providerManageAppointment } from './ProviderManageAppointment';
import { utmParameters } from './UTMParameters.model';
import { UserState as userState } from './UserState.model';
import { providerWorkPeriods } from './ProviderWorkPeriods';
import { providerEditAvailability } from './ProviderEditAvailability';
import { providerManageServices } from './ProviderManageServices';
import { providerManageServiceGroups } from './ProviderManageServiceGroups';
import { providerGallery } from './ProviderGallery';

// Page models
import bookingPage from '../components/pages/BookingPage/BookingPage.model';
import clientAppointmentsList from '../components/pages/ClientAppointmentsList/ClientAppointmentsList.model';
import consumerProfilePage from '../components/pages/ConsumerProfilePage/ConsumerProfilePage.model';
import fillScheduleGapPage from '../components/pages/FillScheduleGap/FillScheduleGapPage.model';
import passwordResetConfirmPage from '../components/pages/PasswordResetConfirm/PasswordResetConfirmPage.model';
import proProfile from '../components/pages/ProProfile/ProProfile.model';
import proProfileEdit from '../components/pages/provider/profileEdit/ProProfileEdit.model';
import salonProfile from '../components/pages/Salon/SalonProfile.model';
import invitedToBookPage from '../components/pages/client/InvitedToBook/InvitedToBookLandingPage.model';
import proAppointmentDetails from '../components/pages/provider/AppointmentDetails/AppointmentDetails.model';
import proClientList from '../components/pages/provider/client/ProClientList.model';

type RootModelNames = (
  'abTest' |
  'booking' |
  'bookingPage' |
  'clientAppointmentsList' |
  'clientNavBar' |
  'clientPromotions' |
  'consumerProfilePage' |
  'favorites' |
  'fillScheduleGapPage' |
  'invitedToBookPage' |
  'loader' |
  'location' |
  'marketingVideos' |
  'marketplaceProfile' |
  'ncdAndDiscountedPros' |
  'netEarnings' |
  'passwordResetConfirmPage' |
  'paymentMethods' |
  'portals' |
  'previousAppointments' |
  'proCheckoutSettings' |
  'proClientList' |
  'proAppointmentDetails' |
  'proProfile' |
  'proProfileEdit' |
  'providerAvailability' |
  'providerAvailabilityBadgesFromSearch' |
  'providerClients' |
  'providerCalendar' |
  'providerEditAvailability' |
  'profileGalleries' |
  'providerGallery' |
  'providerGiftCard' |
  'providerManageAppointment' |
  'providerOnboarding' |
  'providerProducts' |
  'providerReviews' |
  'providerSearchRank' |
  'profileServiceGroups' |
  'providerManageServices' |
  'providerManageServiceGroups' |
  'providerServiceToPageMapping' |
  'providerTopServices' |
  'providerWorkPeriods' |
  'providerWorkSchedule' |
  'providers' |
  'ratingsSummary' |
  'recentSearches' |
  'recommendations' |
  'referrals' |
  'route' |
  'salonProfile' |
  'search' |
  'searchConfig' |
  'seoData' |
  'showIncentives' |
  'sidebar' |
  'snackbar' |
  'stripeIdentityVerification' |
  'stripeTerminal' |
  'subsBlocker' |
  'trackingId' |
  'user' |
  'userAppointments' |
  'userState' |
  'utmParameters'
);

// Add models here, alphabetically please
const models = {
  abTest,
  booking,
  bookingPage,
  clientAppointmentsList,
  clientNavBar,
  clientPromotions,
  consumerProfilePage,
  favorites,
  fillScheduleGapPage,
  invitedToBookPage,
  loader,
  location,
  marketplaceProfile,
  ncdAndDiscountedPros,
  netEarnings,
  paymentMethods,
  marketingVideos: MarketingVideos,
  passwordResetConfirmPage,
  portals,
  previousAppointments,
  proAppointmentDetails,
  proClientList,
  providers,
  providerManageAppointment,
  providerAvailability,
  providerAvailabilityBadgesFromSearch,
  providerCalendar,
  providerClients,
  providerEditAvailability,
  profileGalleries,
  providerGallery,
  providerGiftCard,
  providerOnboarding,
  providerProducts,
  providerReviews,
  profileServiceGroups,
  providerManageServices,
  providerManageServiceGroups,
  providerServiceToPageMapping,
  providerTopServices,
  providerWorkPeriods,
  providerWorkSchedule,
  ratingsSummary,
  recentSearches,
  recommendations,
  referrals,
  route,
  search,
  searchConfig,
  seoData,
  showIncentives,
  sidebar,
  snackbar,
  stripeIdentityVerification,
  stripeTerminal,
  subsBlocker,
  trackingId,
  user,
  proCheckoutSettings,
  proProfile,
  proProfileEdit,
  salonProfile,
  userAppointments,
  userState,
  providerSearchRank,
  utmParameters,
};

type RootModels = typeof models;

export type RootState = ExtractStates<RootModels>;

export type IRootDispatch = {
  [key in RootModelNames]: ExtractReducersAndEffects<RootModels, RootModels[key]>
} & Dispatch<NonRematchActions>;

type NonRematchActions = Action<'never'>;

/**
 * convertDispatchFromRematch
 *
 * Re-types a mapDispatchToProps function to make it suitable for
 * passing into connect()
 *
 * This causes typechecking of your mapDispatchToProps return value.
 *
 * Usage:
 * export connect(
 *  null,
 *  convertDispatchFromRematch<typeof mapDispatchToProps>(mapDispatchToProps),
 * )(MyComponent);
 *
 */
export const convertDispatchFromRematch = (<K extends (...args: any) => any,
  RG extends { ARG: any, RET: any } =
  ConvertDispatchFromRematch<NonRematchActions, IRootDispatch, K>,
  >(f: RG['ARG']) => (f as RG['RET'])
);

// TODO: get this to work on strict mode
// export default models as ModelDefinitions<typeof models, keyof typeof models>;
export default models;
